if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.hasAttribute('data-pdp-main-slider')) return;
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.hasAttribute('data-pdp-main-slider')) return;
        this.onSwiperThumbnailClick = (event) => {
          const button = event.target.closest('button[data-target]');
          if (button && this.contains(button)) this.setActiveMedia(button.dataset.target, false);
        };
        this.addEventListener('click', this.onSwiperThumbnailClick);
        if (typeof pdpslider === 'function') pdpslider(this);
        this.syncSwiperMedia();
      }

      disconnectedCallback() {
        if (!this.hasAttribute('data-pdp-main-slider')) return;
        this.removeEventListener('click', this.onSwiperThumbnailClick);
        this.destroySwiper();
      }

      destroySwiper() {
        const main = this.querySelector('[data-main-pdp-swiper]')?.swiper;
        const thumbs = main?.thumbs?.swiper;
        if (main && !main.destroyed) main.destroy(true, true);
        if (thumbs && !thumbs.destroyed) thumbs.destroy(true, true);
      }

      updateSwiperMedia(html, featuredMediaId) {
        const sourceGallery = html.querySelector('media-gallery[data-pdp-main-slider]');
        if (!sourceGallery) return;
        this.destroySwiper();
        this.innerHTML = sourceGallery.innerHTML;
        this.elements.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.elements.viewer = this.querySelector('[data-pdp-slider]');
        if (typeof pdpslider === 'function') pdpslider(this);
        if (featuredMediaId) this.setActiveMedia(String(featuredMediaId), false);
        this.syncSwiperMedia();
      }

      syncSwiperMedia() {
        const swiper = this.querySelector('[data-main-pdp-swiper]')?.swiper;
        const activeMedia = swiper?.slides[swiper.activeIndex];
        if (!activeMedia) return;
        Array.from(swiper.slides).forEach((slide) => slide.classList.toggle('is-active', slide === activeMedia));
        this.querySelectorAll('button[data-target]').forEach((button) => {
          if (button.closest('[data-thumb-media-id]')?.dataset.thumbMediaId === activeMedia.dataset.mediaId) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
        if (this.elements.liveRegion && window.accessibilityStrings?.imageAvailable) {
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            String(swiper.realIndex + 1)
          );
        }
        this.playActiveMedia(activeMedia);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.hasAttribute('data-pdp-main-slider')) {
          const swiper = this.querySelector('[data-main-pdp-swiper]')?.swiper;
          if (!swiper || swiper.destroyed) return;
          const id = String(mediaId).split('-').pop();
          const index = Array.from(swiper.slides).findIndex(
            (slide) => !slide.classList.contains('swiper-slide-duplicate') && slide.dataset.mediaId === id
          );
          if (index < 0) return;
          if (swiper.params.loop) {
            const slideIndex = Number(swiper.slides[index].dataset.swiperSlideIndex ?? index);
            swiper.slideToLoop(slideIndex);
          } else {
            swiper.slideTo(index);
          }
          this.syncSwiperMedia();
          return;
        }
        // if(this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) || this.elements.viewer.querySelector('[data-media-id]'))
        // {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        // }
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
